import React, { useEffect } from "react";
import _ from "lodash";
import { EVENT_PLANNING_VOLUME_BY_WEEK } from "../../../plannerConfig";
import {
  VolumeByWeekGrid,
  HeaderCellTitle,
  HeaderDateCell,
  LastYearActualOutboundHeader,
  CenteredCell,
  HeaderTitle,
  SubHeaderContainer,
  SubHeader,
  FigureContainer,
  FigureValue,
  FigureRow,
} from "./styles";
import {
  MultiWeekPlannerMainContainer,
  AssumptionSummaryText,
} from "../multiWeekStyles.common";
import LoadSpinnerOverlay from "../../LoadSpinner";
import { asFormattedValue } from "../../../helpers";

const EMPHASIS_ATTRIBUTE_PATHS = ["Unmet Pick Volume", "Total Unmet Hours"];

const PeakDayView = ({
  api,
  store,
  fromDate,
  toDate,
  fromDateFilter,
  toDateFilter,
  durationAggregator,
  selectedScenarios,
}) => {
  const { data, loadingQueue } = store[EVENT_PLANNING_VOLUME_BY_WEEK];

  const getPeakDayData = () => {
    const { get } = api;
    const buildSelectedScenariosString = selectedScenarios
      .map((scenario) => `scenario_id=${scenario.id}`)
      .join("&");
    if (fromDate && toDate) {
      get([EVENT_PLANNING_VOLUME_BY_WEEK], {
        routeParams: {
          fromDate,
          toDate,
          fromDateFilter,
          toDateFilter,
          durationAggregator,
          scenarios: `${buildSelectedScenariosString}&`,
        },
      });
    }
  };

  useEffect(() => {
    getPeakDayData();
  }, [
    fromDate,
    toDate,
    fromDateFilter,
    toDateFilter,
    durationAggregator,
    selectedScenarios,
  ]);

  if (loadingQueue.length || _.isEmpty(data)) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadSpinnerOverlay
          text="Volume By Week Data Loading..."
          spinning={true}
        />
      </div>
    );
  }

  const {
    TableA: {
      title,
      data: {
        "Last Year Actual Outbound": lastYearActualOutbound,
        "Plan Projection": planProjection,
        dates,
        Volume,
        Budget,
      },
    },
  } = data;

  const formatSectionedData = (sectionedData) => {
    const dataKeys = Object.keys(sectionedData);
    return {
      dataKeys,
      sectionedData,
    };
  };

  const {
    dataKeys: volumeSubHeaders,
    sectionedData: volumeData,
  } = formatSectionedData(Volume);

  const {
    dataKeys: budgetSubHeaders,
    sectionedData: budgetData,
  } = formatSectionedData(Budget);

  const {
    dataKeys: projectionDataKeys,
    sectionedData: projectionData,
  } = formatSectionedData(planProjection);

  return (
    <MultiWeekPlannerMainContainer>
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <AssumptionSummaryText style={{ marginTop: "1em" }}>
          {title}
        </AssumptionSummaryText>
        <VolumeByWeekGrid>
          <div
            style={{
              gridArea: "volumeByWeekHeader",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <HeaderCellTitle>{title}</HeaderCellTitle>
            <div style={{ display: "flex", flex: 5 }}>
              {dates.map((date) => {
                return <HeaderDateCell key={date}>{date}</HeaderDateCell>;
              })}
            </div>
          </div>
          <div
            style={{
              gridArea: "lastYearActualOutbound",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <LastYearActualOutboundHeader>
              Last Year Actual Outbound
            </LastYearActualOutboundHeader>
            <div style={{ display: "flex", flex: 5 }}>
              {dates.map((date) => {
                return (
                  <CenteredCell key={date}>
                    {asFormattedValue(lastYearActualOutbound[date])}
                  </CenteredCell>
                );
              })}
            </div>
          </div>
          <div
            style={{
              gridArea: "volume",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <HeaderTitle>Volume</HeaderTitle>
            <SubHeaderContainer>
              {volumeSubHeaders.map((volumeSubHeader) => {
                return (
                  <SubHeader
                    highlighted={EMPHASIS_ATTRIBUTE_PATHS.includes(
                      volumeSubHeader
                    )}
                    key={volumeSubHeader}
                  >
                    {volumeSubHeader}
                  </SubHeader>
                );
              })}
            </SubHeaderContainer>
            <FigureContainer>
              {volumeSubHeaders.map((volumeSubHeader) => {
                return (
                  <FigureRow>
                    {dates.map((date) => {
                      return (
                        <FigureValue
                          highlighted={EMPHASIS_ATTRIBUTE_PATHS.includes(
                            volumeSubHeader
                          )}
                        >
                          {asFormattedValue(volumeData[volumeSubHeader][date])}
                        </FigureValue>
                      );
                    })}
                  </FigureRow>
                );
              })}
            </FigureContainer>
          </div>
          <div
            style={{
              gridArea: "budget",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <HeaderTitle>Hours</HeaderTitle>
            <SubHeaderContainer>
              {budgetSubHeaders.map((budgetSubHeader) => {
                return (
                  <SubHeader
                    highlighted={EMPHASIS_ATTRIBUTE_PATHS.includes(
                      budgetSubHeader
                    )}
                    key={budgetSubHeader}
                  >
                    {budgetSubHeader}
                  </SubHeader>
                );
              })}
            </SubHeaderContainer>
            <FigureContainer>
              {budgetSubHeaders.map((budgetSubHeader) => {
                return (
                  <FigureRow>
                    {dates.map((date) => {
                      return (
                        <FigureValue
                          highlighted={EMPHASIS_ATTRIBUTE_PATHS.includes(
                            budgetSubHeader
                          )}
                          key={date}
                        >
                          {asFormattedValue(budgetData[budgetSubHeader][date])}
                        </FigureValue>
                      );
                    })}
                  </FigureRow>
                );
              })}
            </FigureContainer>
          </div>
          <div
            style={{
              gridArea: "projection",
              display: "flex",
              flexDirection: "row",
            }}
          >
            <HeaderTitle>OWR</HeaderTitle>
            <SubHeaderContainer>
              {projectionDataKeys.map((projection) => (
                <SubHeader>{projection}</SubHeader>
              ))}
            </SubHeaderContainer>
            <FigureContainer>
              {projectionDataKeys.map((projection) => (
                <FigureRow>
                  {dates.map((date) => (
                    <FigureValue>
                      {projectionData[projection][date]}
                    </FigureValue>
                  ))}
                </FigureRow>
              ))}
            </FigureContainer>
          </div>
          <div style={{ gridArea: "space1", backgroundColor: "#FAFAFA" }} />
          <div style={{ gridArea: "space2", backgroundColor: "#FAFAFA" }} />
          <div style={{ gridArea: "space3", backgroundColor: "#FAFAFA" }} />
          <div style={{ gridArea: "space4", backgroundColor: "#FAFAFA" }} />
        </VolumeByWeekGrid>
      </div>
    </MultiWeekPlannerMainContainer>
  );
};

export default PeakDayView;
